import { isFunction } from 'lodash';
import { Ellipsis, Link2, ListFilter, Plus } from 'lucide-react';
import { MouseEvent } from 'react';

import { Tooltip } from '@/components/tooltip';
import { TabsList, TabsRoot, TabsTrigger } from '@/components/ui/tabs';
import { useUserSettings } from '@/hooks/use-user-settings';
import { SavedItemModel } from '@/lib/models/saved-item/types';
import { sortBySortOrder } from '@/lib/models/saved-item/utils';
import { tv } from '@/utils/styles';

export interface ShipmentListTabsProps {
  savedItems: SavedItemModel[];
  currentTab?: string;
  onValueChange?: (newValue: string) => void;
  onTabClick?: (value?: SavedItemModel) => void;
  onClickCreate?: () => void;
  onClickFilters?: () => void;
  areFiltersVisible?: boolean;
  onClickSettings?: () => void;
  onClickShare?: () => void;
}

export const DEFAULT_SHIPMENTS_TAB = 'all';

const ShipmentsTabs = ({
  savedItems,
  currentTab = DEFAULT_SHIPMENTS_TAB,
  onValueChange,
  onTabClick,
  onClickCreate,
  onClickFilters,
  areFiltersVisible,
  onClickSettings,
  onClickShare,
}: ShipmentListTabsProps) => {
  const { shipmentViewSorting } = useUserSettings();
  const tabSorting = shipmentViewSorting?.value || [];

  const shouldShowShare = isFunction(onClickShare) && currentTab !== DEFAULT_SHIPMENTS_TAB;
  const sortedTabs = sortBySortOrder(savedItems, tabSorting);

  const handleClick = (filter: SavedItemModel) => {
    if (isFunction(onTabClick)) {
      onTabClick(filter);
    }
  };

  const handleValueChange = (value: string) => {
    if (isFunction(onValueChange)) {
      onValueChange(value);
    }
  };

  const handleReset = () => {
    if (isFunction(onTabClick)) {
      onTabClick();
    }
  };

  const handleClickCreate = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    if (onClickCreate) {
      onClickCreate();
    }
  };

  const handleClickFilters = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    if (isFunction(onClickFilters)) {
      onClickFilters();
    }
  };

  const handleClickSettings = (e: MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    if (isFunction(onClickSettings)) {
      onClickSettings();
    }
  };

  const { base, buttonWrapper, settings } = styles();

  return (
    <div className={base()}>
      <TabsRoot value={currentTab} onValueChange={handleValueChange} buffer={32}>
        <TabsList>
          <TabsTrigger value={DEFAULT_SHIPMENTS_TAB} onClick={handleReset}>
            All shipments
          </TabsTrigger>
          {sortedTabs?.map((filter) => {
            const { label, id = '' } = filter;
            return (
              <TabsTrigger key={id} value={label} onClick={() => handleClick(filter)}>
                {label}
              </TabsTrigger>
            );
          })}
          <Tooltip content="Create new view">
            <button
              aria-label="Create view"
              type="button"
              onClick={handleClickCreate}
              className={buttonWrapper({ className: 'translate-y-1/2' })}
            >
              <Plus size={16} />
            </button>
          </Tooltip>
        </TabsList>
      </TabsRoot>
      <div className={settings()}>
        {shouldShowShare ? (
          <button
            onClick={onClickShare}
            type="button"
            className="flex items-center text-baseSm text-lightBlue-600 underline-offset-2 hover:underline"
          >
            Share
            <Link2 size={16} className="ml-1.5" />
          </button>
        ) : null}
        <Tooltip content="Manage views">
          <button aria-label="Manage views" onClick={handleClickSettings} type="button" className={buttonWrapper()}>
            <Ellipsis size={16} />
          </button>
        </Tooltip>
        <Tooltip content={`${areFiltersVisible ? 'Show' : 'Hide'} filters`}>
          <button
            aria-label="Show filters"
            onClick={handleClickFilters}
            type="button"
            title="Show filters"
            className={buttonWrapper()}
          >
            <ListFilter size={16} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'relative pr-14',
    buttonWrapper: 'relative inline-grid size-5 place-items-center rounded-md hover:bg-lightBlue-500 hover:text-white',
    settings: 'absolute bottom-0 right-0 flex items-center gap-2 border-b border-grey-300 pb-2.5 pl-2',
  },
});

export { ShipmentsTabs };
